import { Alert, Button, Icon, Text, VStack } from '@chakra-ui/react'
import { BgColors, FontFamily, TextColors } from 'commons/design-system/tokens'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { FiRefreshCw, FiX } from 'react-icons/fi'
import { MdPriorityHigh } from 'react-icons/md'

type PaymentErrorStageProps = {
	error: {
		title: string
		message: string
	}
	onRetry: () => void
	onChoosePaymentMethod: () => void
}

export const PaymentErrorStage = ({
	error,
	onRetry,
	onChoosePaymentMethod
}: PaymentErrorStageProps) => {
	const { t } = useTranslation()
	const titleId = useId()
	const descriptionId = useId()

	return (
		<VStack align='stretch' gap={4} pt={6} w='full'>
			<Alert.Root
				status='error'
				role='alert'
				aria-labelledby={titleId}
				aria-describedby={descriptionId}
				alignItems='flex-start'
				gap={{ base: 3, md: 5 }}
				p={{ base: 4, md: 6 }}
				border='1px solid'
				borderColor='#ff3158'
				borderRadius='l'
				bg='#fff0f3'
			>
				<Alert.Indicator
					boxSize={{ base: '40px', md: '48px' }}
					flexShrink={0}
					borderRadius='full'
					bg='#ff3158'
					color={TextColors.OPPOSITE_PRIMARY}
					justifyContent='center'
					alignItems='center'
					boxShadow='0 3px 8px rgba(255, 49, 88, 0.2)'
					aria-hidden='true'
				>
					<Icon as={MdPriorityHigh} boxSize={7} />
				</Alert.Indicator>
				<Alert.Content gap={2} minW={0}>
					<Alert.Title
						as='h2'
						id={titleId}
						fontSize={{ base: 'xl', md: '2xl' }}
						lineHeight={1.25}
						fontWeight={700}
						color='#73051c'
						fontFamily={FontFamily.DEGULAR}
					>
						{error.title}
					</Alert.Title>
					<Alert.Description
						id={descriptionId}
						fontFamily={FontFamily.MONTSERRAT}
						fontSize={{ base: 'sm', md: 'md' }}
						fontWeight={400}
						lineHeight={1.6}
						color={TextColors.SECONDARY}
					>
						<Text>{error.message}</Text>
						<Text>{t('phone_form.payment_error_recovery_hint')}</Text>
					</Alert.Description>
				</Alert.Content>
			</Alert.Root>
			<Button
				type='button'
				bg={BgColors.OPPOSITE_Z3}
				color={TextColors.OPPOSITE_PRIMARY}
				borderRadius='m'
				minH='56px'
				h='auto'
				py={4}
				px={4}
				gap={3}
				fontSize='md'
				fontWeight='bold'
				fontFamily={FontFamily.MONTSERRAT}
				whiteSpace='normal'
				_hover={{ bg: BgColors.OPPOSITE_Z2 }}
				_focusVisible={{
					outline: '2px solid',
					outlineColor: BgColors.OPPOSITE_Z2,
					outlineOffset: 3
				}}
				onClick={onRetry}
			>
				<Icon as={FiRefreshCw} boxSize={6} flexShrink={0} aria-hidden='true' />
				{t('phone_form.payment_error_retry_button')}
			</Button>
			<Button
				type='button'
				variant='outline'
				bg='transparent'
				color={TextColors.SECONDARY}
				border='1px solid'
				borderColor={TextColors.TERTIARY}
				borderRadius='m'
				minH='56px'
				h='auto'
				py={4}
				px={4}
				gap={3}
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight='bold'
				fontFamily={FontFamily.MONTSERRAT}
				whiteSpace='normal'
				_hover={{ bg: BgColors.Z3 }}
				_focusVisible={{
					outline: '2px solid',
					outlineColor: BgColors.OPPOSITE_Z2,
					outlineOffset: 3
				}}
				onClick={onChoosePaymentMethod}
			>
				<Icon as={FiX} boxSize={6} flexShrink={0} aria-hidden='true' />
				{t('phone_form.payment_error_choose_method')}
			</Button>
		</VStack>
	)
}
